import {zodResolver} from "@hookform/resolvers/zod";
import {FloatingField} from "@mytour/ui/form";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Button, Form, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateBusinessDescriptor} from "../../query/companySettingsQueries";

const PUBLIC_JOB_LABEL_MAX_LENGTH = 100;

const publicJobLabelSchema = z.object({
  publicJobLabel: z.string().max(PUBLIC_JOB_LABEL_MAX_LENGTH, "Intitulé trop long"),
});
type PublicJobLabelFormData = z.infer<typeof publicJobLabelSchema>;

type Props = {
  /** Current override value (`null`/empty → the public page falls back to jobTitle). */
  publicJobLabel: string | null;
  /** App-behaviour job title, shown as the default the public page uses when empty. */
  jobTitle: string | null;
};

/**
 * RDV en ligne — public job label editing block (#564).
 *
 * Lets the pro set the job shown on the public booking page (`/pro/:slug`),
 * decoupled from the internal `jobTitle`. The field is an OVERRIDE: left empty,
 * the public page falls back to `jobTitle` (shown here as a hint). Saved through
 * the existing `UpdateBusinessDescriptorPayload` / `POST business/descriptor/`;
 * the section sends ONLY `publicJobLabel` (backend field-guarded, so jobTitle /
 * description / etc. are never touched), and `"" → null` reverts to the default.
 */
export function PublicJobLabelSection({publicJobLabel, jobTitle}: Props) {
  const {updateBusinessDescriptor, isUpdatingBusinessDescriptor} = useUpdateBusinessDescriptor();
  const disabled = isUpdatingBusinessDescriptor;

  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<PublicJobLabelFormData>({
    resolver: zodResolver(publicJobLabelSchema),
    defaultValues: {publicJobLabel: publicJobLabel ?? ""},
  });

  return (
    <Form onSubmit={handleSubmit(handleSave)}>
      <h4>Intitulé public</h4>
      <p className="text-muted small">
        L'intitulé de votre métier affiché sur votre page publique.
      </p>

      <FloatingField
        name="publicJobLabel"
        label="Intitulé public"
        control={control}
        error={errors.publicJobLabel}
        disabled={disabled}
      />
      <p className="text-muted small mt-1">
        {jobTitle
          ? `Laissez vide pour afficher votre métier (« ${jobTitle} »).`
          : "Laissez vide pour n'afficher aucun intitulé."}
      </p>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingBusinessDescriptor ? <Spinner size="sm" /> : null}
        {isUpdatingBusinessDescriptor ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function handleSave(data: PublicJobLabelFormData) {
    try {
      await updateBusinessDescriptor({publicJobLabel: data.publicJobLabel});
      toast.success("Intitulé public enregistré");
    } catch {
      toast.error("Erreur lors de la sauvegarde de l'intitulé public");
    }
  }
}
